/* eslint-disable sonarjs/no-duplicate-string */
import { styled } from '@mui/material';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import MuiRadioGroup, { RadioGroupProps } from '@mui/material/RadioGroup';
import { CustomRadioProps, Radio, RadioButtonSize } from './Radio';

type RadioGroupLayout = 'column' | 'row' | 'list';

type RadioGroupButton = Omit<CustomRadioProps, 'fullWidth' | 'size' | 'onCheck'> & {
    'data-testid'?: string;
    value: string;
};

interface CustomRadioGroupProps extends Omit<RadioGroupProps, 'column' | 'row' | 'value' | 'onChange'> {
    border?: boolean;
    buttons: RadioGroupButton[];
    fullWidth?: boolean;
    layout?: RadioGroupLayout;
    size?: RadioButtonSize;
    value?: string;
    onChange: (value: string) => void;
}

const StyledRadioGroup = styled(MuiRadioGroup, { shouldForwardProp: (prop) => prop !== 'fullWidth' })<{
    fullWidth: boolean;
}>(({ fullWidth, theme }) => ({
    gap: theme.spacing(2),
    width: fullWidth ? '100%' : 'unset',
}));

const StyledRadioList = styled(Box, { shouldForwardProp: (prop) => prop !== 'fullWidth' })<{ fullWidth: boolean }>(
    ({ fullWidth, theme }) => ({
        alignItems: 'stretch',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.constants.borderRadius,
        display: 'flex',
        flexDirection: 'column',
        width: fullWidth ? '100%' : 'unset',
    }),
);

const StyledRadio = styled(Radio, { shouldForwardProp: (prop) => prop !== 'position' && prop !== 'size' })<{
    position: 'first' | 'last' | 'middle';
    size: RadioButtonSize;
}>(({ position, size, theme }) => ({
    borderRadius:
        position === 'first'
            ? `${theme.constants.borderRadius}px ${theme.constants.borderRadius}px 0 0`
            : position === 'last'
              ? `0 0 ${theme.constants.borderRadius}px ${theme.constants.borderRadius}px`
              : 0,
    padding: size === 'small' ? theme.spacing(1, 1.5) : theme.spacing(2, 2.5),
    width: '100%',
}));

export const RadioGroup: React.FC<CustomRadioGroupProps> = ({
    border = false,
    buttons,
    fullWidth = false,
    layout = 'column',
    size = 'small',
    value,
    onChange,
    ...props
}) => {
    const displayedButtons = buttons.map((b, i) => (
        <Radio
            key={`radio-group-button-${i}`}
            border={border}
            checked={b.value === value}
            data-testid={b['data-testid']}
            disabled={b.disabled}
            endIcon={b.endIcon}
            fullWidth={layout === 'row' ? false : fullWidth} // Can't be fullwidth if row
            label={b.label}
            size={size}
            onCheck={() => onChange(b.value)}
        />
    ));

    const displayedList = buttons.map((b, i) => (
        <Box key={`radio-group-button-${i}`}>
            <StyledRadio
                checked={b.value === value}
                data-testid={b['data-testid']}
                disabled={b.disabled}
                endIcon={b.endIcon}
                fullWidth={fullWidth}
                label={b.label}
                position={i === 0 ? 'first' : i === buttons.length - 1 ? 'last' : 'middle'}
                size={size}
                onCheck={() => onChange(b.value)}
            />
            {i < buttons.length - 1 && <Divider />}
        </Box>
    ));

    return layout === 'list' ? (
        <StyledRadioList fullWidth={fullWidth}>{displayedList}</StyledRadioList>
    ) : (
        <StyledRadioGroup fullWidth={fullWidth} row={layout === 'row'} value={value} {...props}>
            {displayedButtons}
        </StyledRadioGroup>
    );
};
